<script setup lang="ts">
import { computed, ref } from 'vue'
import type { BookPageConfig } from '../configs/types'
import { t } from '@/i18n/utils'
import { formatCompactNumber } from '@/utils'

const props = defineProps<{
  book: GetContentGetcontentdetailResponse['data']
  config: BookPageConfig
  coverUrl?: string
  introduce?: string
  paragraphs: string[]
  currentContentId?: string | null
  popupTOC: boolean
  bannerShow: boolean
  isInShelf?: boolean
  shelfLoading?: boolean
}>()
const emit = defineEmits<{
  (event: 'update:popup-t-o-c', value: boolean): void
  (event: 'goto-tags', tagId: string): void
  (event: 'open-toc'): void
  (event: 'read'): void
  (event: 'back'): void
  (event: 'toggle-shelf'): void
  (event: 'audio'): void
}>()
const expanded = ref(false)
const status = computed(() => props.book.finishStatus ? props.config.texts.completed : props.config.texts.ongoing)
const popupTOCModel = computed({
  get: () => props.popupTOC,
  set: value => emit('update:popup-t-o-c', value),
})
</script>

<template>
  <div class="sprig-book" :class="{ 'with-download': bannerShow }">
    <nav class="book-nav">
      <button type="button" aria-label="Back" @click="emit('back')">
        <i-icon-park-outline-left />
      </button>
    </nav>

    <section class="book-hero">
      <img class="book-cover" :src="coverUrl" :alt="book.contentName">
      <h1>{{ book.contentName }}</h1>
      <p class="byline">
        {{ book.authorInfo?.author }}
      </p>
      <div v-if="book.tagList?.length" class="book-tags">
        <div class="book-tags-track">
          <button v-for="tag in book.tagList" :key="tag.id" class="book-tag" type="button" @click="emit('goto-tags', tag.id || '')">
            {{ tag.name }}
          </button>
        </div>
      </div>
      <div class="book-stats">
        <div><strong>{{ formatCompactNumber(book.viewUsers || 0) }}</strong><span>{{ t('NovelSprigReaders') }}</span></div>
        <button type="button" @click="emit('open-toc')">
          <strong>{{ book.chapterNum ?? '—' }}</strong><span>{{ t('ChapterList') }}</span>
        </button>
        <div><strong>{{ status }}</strong><span>{{ t('NovelSprigStatus') }}</span></div>
      </div>
    </section>

    <section class="about-card">
      <h2>{{ t('NovelSprigAboutStory') }}</h2>
      <p class="story-intro" :class="{ collapsed: !expanded }">
        {{ book.introduce }}
      </p>
      <button v-if="book.introduce" class="expand-button" type="button" :aria-expanded="expanded" @click="expanded = !expanded">
        <span>{{ expanded ? t('ShowLess') : t('ReadMore') }}</span>
        <svg class="expand-chevron" :class="{ expanded }" viewBox="0 0 16 16" aria-hidden="true">
          <path d="m4 6 4 4 4-4" />
        </svg>
      </button>
    </section>

    <!-- Sample 章节样本 -->
    <div class="mt-5 mx-5 pb-5">
      <div class="flex space-x-4 items-center justify-between">
        <p class="text-17px font-bold whitespace-nowrap text-n-text-1">
          {{ config.texts.catalogue }}
        </p>
        <div class="w-70vw">
          <div
            class="text-13px flex items-center justify-end cursor-pointer text-n-text-2"
            @click="emit('open-toc')"
          >
            <p class="line-clamp-1">
              {{ config.texts.latestChapter }}：{{ book.lastChapter?.chapterName }}
            </p>
            <i-icon-park-outline-right class="w-4 h-4" />
          </div>
        </div>
      </div>

      <!-- 引导语 -->
      <div v-if="introduce" class="chapter-introduce lh-30px mt-20px">
        <p v-html="introduce" />
      </div>

      <!-- 样本文本 -->
      <div class="lh-30px text-n-text-2">
        <p class="mt-4">
          {{ book.firstChapter?.chapterName }}
        </p>
        <p v-for="(item, index) in paragraphs.slice(0, 16)" :key="index" class="mt-4">
          {{ item }}
        </p>
      </div>

      <!-- 分割线 -->
      <div class="mt-10 flex items-center justify-center space-x-2">
        <span class="w-8 h-1px bg-[#999999]" />
        <span class="text-14px text-[#999999]">
          {{ config.texts.readFirstChapter }}
        </span>
        <span class="w-8 h-1px bg-[#999999]" />
      </div>
    </div>

    <BasicFixedBottom>
      <div class="book-actions">
        <button class="library-button" type="button" :disabled="shelfLoading" @click="emit('toggle-shelf')">
          {{ isInShelf ? '✓' : '+' }} {{ t('LibraryTab') }}
        </button>
        <button class="read-button" type="button" @click="emit('read')">
          {{ book.readFlag ? config.texts.continueReading : config.texts.startReading }} →
        </button>
        <button v-if="book.hasAudio === 1" class="audio-button" type="button" aria-label="Audio" @click="emit('audio')">
          <i-icon-park-outline-headset />
        </button>
      </div>
    </BasicFixedBottom>
    <BizPopupBookTOC
      v-if="book.firstChapterContentUrl" v-model="popupTOCModel" :book="book"
      :content-id="book.id" :chapter-id="book.firstChapter?.id" :lock-icon-color="config.theme.tocLockColor"
    />
  </div>
</template>

<style lang="scss" scoped>
.sprig-book { position: relative; color: #1F2937; background: #F5FDF9; }
.with-download { padding-top: 60px; }
.book-nav { position: absolute; top: 0; left: 0; right: 0; z-index: 2; height: 44px; padding: 0 12px; display: flex; align-items: center; background: transparent; }
.with-download .book-nav { top: 60px; }
button { cursor: pointer; border: 0; background: none; color: inherit; font: inherit; }
.book-nav button { width: 32px; height: 36px; display: flex; align-items: center; justify-content: center; font-size: 21px; }
.book-hero { padding: 44px 20px 24px; text-align: center; background: linear-gradient(180deg, #E8FBF2 0%, #F5FDF9 100%); }
.book-cover { display: block; width: 120px; height: 174px; object-fit: cover; margin: 0 auto; border-radius: 9px; box-shadow: 0 15px 24px #1F29371A; }
h1 { margin: 25px 0 10px; font-size: 24px; line-height: 1.25; font-weight: 800; overflow-wrap: anywhere; }
.byline { font-size: 13px; line-height: 20px; color: #4B5563; }
.book-tags { margin: 24px 0; overflow-x: auto; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
.book-tags::-webkit-scrollbar { display: none; }
.book-tags-track { display: flex; align-items: center; justify-content: center; gap: 6px; width: max-content; min-width: 100%; }
.book-tag { flex-shrink: 0; padding: 6px 10px; border-radius: 18px; background: #D1FAE5; color: #059669; font-size: 12px; line-height: 18px; white-space: nowrap; }
.book-stats { display: flex; margin-top: 20px; }
.book-stats > * { flex: 1; min-width: 0; padding: 0 3px; }
.book-stats strong, .book-stats span { display: block; }
.book-stats strong { font-size: 19px; line-height: 27px; font-weight: 800; overflow-wrap: anywhere; }
.book-stats span { margin-top: 6px; font-size: 12px; color: #4B5563; }
.about-card { margin: 0 16px 16px; padding: 22px 20px; background: #FFFFFF; border-radius: 22px; }
h2 { margin-bottom: 15px; font-size: 17px; font-weight: 800; }
.chapter-introduce { color: #059669; }
.story-intro { white-space: pre-line; overflow-wrap: anywhere; font-size: 14px; line-height: 24px; color: #4B5563; }
.collapsed { display: -webkit-box; -webkit-line-clamp: 7; -webkit-box-orient: vertical; overflow: hidden; }
.expand-button { display: inline-flex; align-items: center; gap: 4px; line-height: 18px; margin-top: 6px; padding: 0; border-radius: 20px; background: transparent; color: #10B981; font-size: 12px; }
.expand-chevron { width: 12px; height: 12px; flex-shrink: 0; fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }
.expand-chevron.expanded { transform: rotate(180deg); }
.book-actions { display: flex; align-items: center; padding: 12px 16px 6px; background: #FFFFFF; }
.library-button, .read-button { min-height: 44px; flex: 1; padding: 8px; border-radius: 24px; font-size: 14px; line-height: 20px; }
.library-button { margin-right: 9px; border: 1px solid #A7F3D0; background: #D1FAE5; color: #10B981; }
.library-button:disabled { opacity: .5; cursor: wait; }
.read-button { background: linear-gradient(90deg, #10B981 0%, #059669 100%); color: #FFFFFF; font-weight: 700; }
.audio-button { margin-left: 8px; font-size: 22px; }
button:focus-visible { outline: 2px solid #10B981; outline-offset: 2px; }
</style>
